// A page in the frame, as an iOS screen is: a toolbar on top (a back link to where it came from, its actions), then
// its large title, which slides into the toolbar as the page scrolls. The browser names the page after it.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../auth';
import { useAccount } from '../queries';
import { Avatar, cx } from '../ui';
import { CaretLeftIcon } from '../ui/icons';
import s from './Page.module.css';
import { usePageTitle } from './title';

export interface Back {
  to: string;
  /** Where it goes: "Garage", "R34". */
  label: string;
}

export function Page({
  title,
  tab,
  subtitle,
  back,
  actions,
  headActions,
  root = false,
  width = 'regular',
  largeTitle = true,
  children,
}: {
  title: string;
  /** The browser's name for the page, when it says more than the title: "R34: Settings". */
  tab?: string;
  subtitle?: ReactNode;
  back?: Back;
  /** In the toolbar, at its end. */
  actions?: ReactNode;
  /** Beside the large title. */
  headActions?: ReactNode;
  /** A tab's own page: on a phone, the account's avatar is in its toolbar. */
  root?: boolean;
  width?: 'regular' | 'narrow' | 'wide';
  /** Without one, the title is in the toolbar from the start. */
  largeTitle?: boolean;
  children: ReactNode;
}) {
  usePageTitle(tab ?? title);
  const heading = useRef<HTMLHeadingElement>(null);
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const h = heading.current;
    if (!h || !largeTitle) {
      return;
    }
    const watch = new IntersectionObserver(([entry]) => setCompact(entry ? !entry.isIntersecting : false), {
      rootMargin: '-56px 0px 0px 0px',
    });
    watch.observe(h);
    return () => watch.disconnect();
  }, [largeTitle]);
  return (
    <>
      <header className={cx(s.toolbar, compact && s.compact, !largeTitle && s.flat)}>
        <div className={s.lead}>
          {back && (
            <Link to={back.to} className={s.back}>
              <CaretLeftIcon size={20} weight="bold" aria-hidden="true" />
              <span>{back.label}</span>
            </Link>
          )}
        </div>
        <div className={s.title} aria-hidden={largeTitle ? true : undefined}>
          {largeTitle ? title : <h1 className={s.toolbarHeading}>{title}</h1>}
        </div>
        <div className={s.trail}>
          {actions}
          {root && <AccountAvatar />}
        </div>
      </header>
      <div className={cx(s.content, width === 'wide' && s.wide, width === 'narrow' && s.narrow)}>
        {largeTitle && (
          <div className={s.head}>
            <div className={s.headRow}>
              <h1 ref={heading} className={s.large} tabIndex={-1}>
                {title}
              </h1>
              {headActions && <div className={s.headActions}>{headActions}</div>}
            </div>
            {subtitle && <p className={s.subtitle}>{subtitle}</p>}
          </div>
        )}
        {children}
      </div>
    </>
  );
}

/** The signed-in owner's initials, going to their account: on a phone, where the sidebar isn't. */
function AccountAvatar() {
  const auth = useAuth();
  const session = auth.session('owners');
  const account = useAccount(session !== null);
  if (!session) {
    return null;
  }
  return (
    <Link to="/account" className={s.avatar} aria-label="Account and help">
      <Avatar name={account.data?.displayName ?? session.name} email={session.email} />
    </Link>
  );
}
